// Workforce Overview → Exceptions: the ATTENDANCE review queue (P3).
//
// The queue, its filters and who may see which rows are all server-side
// (leave-service /attendance/exceptions, scoped like /attendance/days). Every
// row opens the employee-day evidence; closing one needs a reason; a
// correction goes through the existing correction path — which turns into a
// reconciliation notice, not a rewrite, when the day is approved or paid.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { usePromptIfAvailable } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  queryExceptions, resolveException, type AttendanceException, type ExceptionSeverity,
} from '@/services/attendanceService';
import type { PresenceDay } from '@/services/workAssignmentService';

const SEV_TONE: Record<string, string> = {
  info: 'bg-info-soft text-info-strong',
  warning: 'bg-warning-soft text-warning-strong',
  critical: 'bg-danger-soft text-danger-strong',
};

/** The finding types the queue can be filtered by (server vocabulary). */
export const EXCEPTION_TYPES = [
  'missing_clock_in', 'missing_clock_out', 'duplicate_punches', 'multiple_codes', 'unusual_duration',
  'conflicting_sources', 'office_network_mismatch', 'late_sync', 'pre_go_live_policy',
  'on_leave_scanned', 'terminated_scanned', 'inactive_scanned', 'blocked_payroll',
  'blocked_approved', 'blocked_manual', 'correction_requested', 'day_corrected', 'backup_checkin', 'unmatched_employee',
];

const selectCls = 'h-9 rounded-md border border-border bg-card px-2 text-sm text-foreground';

export const AttendanceExceptionsPanel: React.FC<{
  nameOf: (id?: string | null) => string;
  onOpen: (d: PresenceDay) => void;
  onCorrect: (target: { employeeId: string; workDate: string }) => void;
}> = ({ nameOf, onOpen, onCorrect }) => {
  const { t } = useTranslation('admin');
  const prompt = usePromptIfAvailable();
  const [status, setStatus] = useState<'open' | 'resolved' | 'ignored' | 'all'>('open');
  const [severity, setSeverity] = useState<'' | ExceptionSeverity>('');
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<AttendanceException[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    queryExceptions({
      status, severity: severity || undefined, type: type || undefined,
      from: from || undefined, to: to || undefined, limit: 200,
    }).then((r) => { setRows(r.exceptions); setTotal(r.total); })
      .catch((e) => toast.error(getApiErrorMessage(e, t('wa.exc_load_err', { defaultValue: 'Could not load attendance exceptions.' }))))
      .finally(() => setBusy(false));
  }, [status, severity, type, from, to, t]);
  useEffect(load, [load]);

  // A reason is required — closing a finding is a decision someone may have
  // to answer for. Branded prompt, never a native browser dialog.
  const close = async (ex: AttendanceException, next: 'resolved' | 'ignored') => {
    if (!prompt) return;
    const note = await prompt({
      title: next === 'resolved'
        ? t('wa.exc_resolve_title', { defaultValue: 'Resolve this finding' })
        : t('wa.exc_ignore_title', { defaultValue: 'Ignore this finding' }),
      message: t('wa.exc_reason_msg', { defaultValue: 'Say why. This note is the record of the decision.' }),
      label: t('wa.reason', { defaultValue: 'Reason (required, audited)' }),
      required: true,
      multiline: true,
      // QA A15: the requirement is shown in the dialog, and submit waits for it.
      minLength: 5,
      minLengthHint: t('wa.reason_min', { defaultValue: 'At least {{n}} characters' }),
    });
    if (note == null) return;
    try {
      await resolveException(ex.id, next, note.trim());
      toast.success(t('wa.exception_updated', { defaultValue: 'Exception updated.' }));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('wa.exc_update_err', { defaultValue: 'Could not update the exception.' })));
    }
  };

  return (
    <Card><CardContent className="space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-charcoal">
          {t('wa.exc_attendance_title', { defaultValue: 'Attendance review queue' })}
          <span className="ml-2 text-muted-foreground font-normal">{total}</span>
        </h3>
        <Link to="../attendance" className="text-sm text-muted-foreground underline">
          {t('wa.exc_unmatched_link', { defaultValue: 'Unmatched scanner codes are matched on the Attendance page' })}
        </Link>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label>{t('wa.col_status', { defaultValue: 'Status' })}</Label>
          <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            {(['open', 'resolved', 'ignored', 'all'] as const).map((s) => (
              <option key={s} value={s}>{t(`wa.exc_status_${s}`, { defaultValue: s })}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>{t('wa.exc_severity', { defaultValue: 'Severity' })}</Label>
          <select className={selectCls} value={severity} onChange={(e) => setSeverity(e.target.value as typeof severity)}>
            <option value="">{t('wa.all', { defaultValue: 'All' })}</option>
            {(['critical', 'warning', 'info'] as const).map((s) => (
              <option key={s} value={s}>{t(`wa.sev_${s}`, { defaultValue: s })}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>{t('wa.col_type', { defaultValue: 'Type' })}</Label>
          <select className={selectCls} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">{t('wa.all', { defaultValue: 'All' })}</option>
            {EXCEPTION_TYPES.map((k) => (
              <option key={k} value={k}>{t(`wa.exc_type_${k}`, { defaultValue: k.replace(/_/g, ' ') })}</option>
            ))}
          </select>
        </div>
        <div><Label>{t('wa.from', { defaultValue: 'From' })}</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label>{t('wa.to', { defaultValue: 'To' })}</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        {busy && <Loader2 className="mb-2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('wa.exc_none_match', { defaultValue: 'Nothing in the queue matches these filters.' })}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 px-3">{t('wa.col_date', { defaultValue: 'Date' })}</th>
              <th className="py-2 px-3">{t('wa.col_employee', { defaultValue: 'Employee' })}</th>
              <th className="py-2 px-3">{t('wa.exc_severity', { defaultValue: 'Severity' })}</th>
              <th className="py-2 px-3">{t('wa.col_type', { defaultValue: 'Type' })}</th>
              <th className="py-2 px-3">{t('wa.exc_what', { defaultValue: 'What happened' })}</th>
              <th className="py-2 px-3" />
            </tr></thead>
            <tbody>{rows.map((ex) => (
              <tr key={ex.id} className="border-b border-border align-top">
                <td className="py-2 px-3 tabular-nums text-foreground whitespace-nowrap">{ex.work_date || '—'}</td>
                <td className="py-2 px-3 text-foreground whitespace-nowrap">{ex.employee_id ? nameOf(ex.employee_id) : '—'}</td>
                <td className="py-2 px-3">
                  <Badge variant="secondary" className={SEV_TONE[ex.severity] || ''}>
                    {ex.severity === 'critical' && <AlertTriangle className="mr-1 h-3 w-3" aria-hidden />}
                    {t(`wa.sev_${ex.severity}`, { defaultValue: ex.severity })}
                  </Badge>
                </td>
                <td className="py-2 px-3 text-foreground whitespace-nowrap">{t(`wa.exc_type_${ex.type}`, { defaultValue: ex.type.replace(/_/g, ' ') })}</td>
                <td className="py-2 px-3 text-muted-foreground">
                  {ex.message}
                  {ex.status !== 'open' && ex.resolution_note && (
                    <span className="block text-xs">{t('wa.exc_closed_note', { defaultValue: 'Closed: {{note}}', note: ex.resolution_note })}</span>
                  )}
                </td>
                <td className="py-2 px-3 text-right space-x-1 whitespace-nowrap">
                  {ex.employee_id && ex.work_date && (
                    <Button size="sm" variant="ghost" onClick={() => onOpen({
                      employeeId: ex.employee_id, date: ex.work_date, status: 'present', kind: '', kindLabel: '', evidence: {},
                    })}>
                      {t('wa.exc_evidence', { defaultValue: 'Evidence' })}
                    </Button>
                  )}
                  {ex.status === 'open' && ex.employee_id && ex.work_date && (
                    <Button size="sm" variant="outline" onClick={() => onCorrect({ employeeId: ex.employee_id, workDate: ex.work_date })}>
                      {t('wa.correct_attendance', { defaultValue: 'Correct attendance' })}
                    </Button>
                  )}
                  {ex.status === 'open' && prompt && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => close(ex, 'resolved')}>{t('wa.resolve', { defaultValue: 'Resolve' })}</Button>
                      <Button size="sm" variant="ghost" onClick={() => close(ex, 'ignored')}>{t('wa.ignore', { defaultValue: 'Ignore' })}</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </CardContent></Card>
  );
};
